/**
 * Cash-pay / package / membership billing (`BACKLOG.md` M9) — a **genuinely separate engine from
 * insurance RCM**, not a thin variant of it. There is no eligibility check, no claim, no EOB, and no
 * denial workflow anywhere in this module, deliberately: those concepts do not exist for a
 * self-pay cosmetic transaction, and building placeholder versions of them here would misrepresent
 * what this engine actually does to the next reader.
 *
 * Four commercial shapes, all closed-set rather than open text, because each behaves differently
 * for redemption/renewal purposes even though all four are "cash pay":
 *
 *   - `package` — a fixed bundle of pre-paid units/sessions, purchased once, drawn down over time.
 *   - `membership` — a recurring (typically monthly) subscription, usually bundling a unit allowance
 *     that itself may or may not roll over — this module does not model auto-renewal or proration,
 *     only the fact that a membership purchase carries a `billingCadence`.
 *   - `unit_bank` — the general form of `package`: a pre-paid balance of a fungible unit (e.g.
 *     neuromodulator units) with no bundled service structure, drawn down by any injectable
 *     administration of the matching product.
 *   - `one_time_service` — a single cash-pay charge with no unit balance to track at all.
 *
 * `bnpl` (buy-now-pay-later) is one of the four `PAYMENT_METHODS`, not a product type — a package,
 * membership, or one-time service can each be financed through it. This module does not integrate
 * with a real BNPL provider (Cherry, PatientFi, etc.); `bnplProvider` on the purchase record
 * (`server/src/db/schema/aesthetics.ts`) is a free-text field naming which one was used, and nothing
 * here calls out to one.
 */

export const CASH_PAY_PRODUCT_TYPES = [
  'package',
  'membership',
  'unit_bank',
  'one_time_service',
] as const;
export type CashPayProductType = (typeof CASH_PAY_PRODUCT_TYPES)[number];

export const PAYMENT_METHODS = ['card', 'cash', 'bnpl', 'account_credit'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const CASH_PAY_PURCHASE_STATUSES = ['active', 'completed', 'cancelled'] as const;
export type CashPayPurchaseStatus = (typeof CASH_PAY_PURCHASE_STATUSES)[number];

export const BILLING_CADENCES = ['monthly', 'annual'] as const;
export type BillingCadence = (typeof BILLING_CADENCES)[number];

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

export function isCashPayProductType(value: string): value is CashPayProductType {
  return includes(CASH_PAY_PRODUCT_TYPES, value);
}
export function isPaymentMethod(value: string): value is PaymentMethod {
  return includes(PAYMENT_METHODS, value);
}
export function isCashPayPurchaseStatus(value: string): value is CashPayPurchaseStatus {
  return includes(CASH_PAY_PURCHASE_STATUSES, value);
}
export function isBillingCadence(value: string): value is BillingCadence {
  return includes(BILLING_CADENCES, value);
}

/** Does this product type carry a redeemable unit balance at all? `membership` is deliberately
 * excluded even though a membership commonly bundles units in practice — this module does not model
 * a recurring allowance (it would need a renewal/rollover policy this pass does not build), so a
 * membership purchase's `unitsPurchased`/`unitsRemaining` are always `null`. A future pass that adds
 * real membership-allowance tracking is a product decision, not a gap this function should paper
 * over by guessing. */
export function tracksUnitBalance(productType: CashPayProductType): boolean {
  return productType === 'package' || productType === 'unit_bank';
}

export type RedemptionResult =
  | { readonly ok: true; readonly unitsRemaining: string }
  | { readonly ok: false; readonly reason: 'insufficient_balance' | 'no_unit_balance' };

/**
 * Redeem `requested` units against a purchase carrying `unitsRemaining` (or `null` for a purchase
 * that never tracked a balance — `tracksUnitBalance` is `false`, or it does track one but the row
 * predates this function ever running against it). Pure and decimal-string-in/decimal-string-out,
 * matching `columns.ts`'s own reasoning for why every quantity in this schema is read back as a
 * string rather than a JS `number`: floating-point arithmetic on money-adjacent quantities is exactly
 * the kind of silent drift a `numeric` column exists to prevent, and converting to `number` here
 * would reintroduce it one call removed from the column type.
 *
 * **Never goes negative.** A request for more than the remaining balance is refused outright rather
 * than clamped to zero — clamping would silently discard the caller's own accounting of how many
 * units they meant to redeem, and "the balance went to exactly zero" and "the balance would have gone
 * negative" are different facts a route needs to tell apart (a full redemption is ordinary; an
 * over-redemption attempt is worth surfacing to the person entering it, since it usually means a
 * stale UI or a double-submit). The schema's own `aesthetics_cash_pay_purchases_units_remaining_check`
 * CHECK is the belt to this function's braces — this function is what lets a route return a real
 * `402`/`409`-shaped refusal instead of relying on a raw constraint-violation error reaching the
 * caller as an opaque 500.
 */
export function redeemUnits(unitsRemaining: string | null, requested: string): RedemptionResult {
  if (unitsRemaining === null) {
    return { ok: false, reason: 'no_unit_balance' };
  }
  const remaining = Number(unitsRemaining);
  const amount = Number(requested);
  if (amount > remaining) {
    return { ok: false, reason: 'insufficient_balance' };
  }
  // Fixed to two decimal places, matching `numeric(10, 2)`'s own scale
  // (`server/src/db/schema/aesthetics.ts`) — computing in `number` and re-stringifying at a
  // different precision than the column stores would drift the two representations apart.
  return { ok: true, unitsRemaining: (remaining - amount).toFixed(2) };
}
